import React, { createContext, type Dispatch, type ReactNode, useContext, useReducer } from 'react';

import { initialState } from './constants';
import type { PreviewPanelsAction, PreviewPanelsApiState } from './types';

export const PreviewPanelsContext = createContext<
	| {
			state: PreviewPanelsApiState;
			dispatch: Dispatch<PreviewPanelsAction>;
	  }
	| undefined
>(undefined);

export const previewPanelsReducer = (
	state: PreviewPanelsApiState,
	action: PreviewPanelsAction,
): PreviewPanelsApiState => {
	switch (action.type) {
		case 'SET_PANEL_WIDTH':
			return { ...state, width: action.width };
		case 'CLOSE':
			// Action explicitly called in order to close the active panel.
			action.onClose?.();
			state.closePanelSlot();
			return initialState;
		case 'OPEN':
			// Action explicitly called in order to open a new panel.
			const { params } = action;

			// If the panel is already open, do nothing.
			if (state.panel.id === params.panel.id) {
				return state;
			}

			// Close the active panel.
			state.onClose();
			state.closePanelSlot();

			// Open the new panel.
			state.openPanelSlot();
			params.onOpen?.();
			return {
				...state,
				panel: params.panel,
				width: params.width || initialState.width,
				onOpen: params.onOpen || initialState.onOpen,
				onClose: params.onClose || initialState.onClose,
			};
		default:
			return state;
	}
};

export const PreviewPanelsProvider = ({
	productHandleOpen,
	productHandleClose,
	children,
}: {
	productHandleOpen: () => void;
	productHandleClose: () => void;
	children: ReactNode;
}) => {
	initialState.closePanelSlot = productHandleClose;
	initialState.openPanelSlot = productHandleOpen;

	const [state, dispatch] = useReducer(previewPanelsReducer, initialState);
	return (
		<PreviewPanelsContext.Provider value={{ state, dispatch }}>
			{children}
		</PreviewPanelsContext.Provider>
	);
};

// Hook to use the context
export const useGetPreviewPanelsContext = () => {
	const context = useContext(PreviewPanelsContext);
	return context;
};

// Context selectors
export const useIsPreviewPanelOpen = (panelId?: string): boolean => {
	const context = useGetPreviewPanelsContext();
	return panelId ? context?.state.panel.id === panelId : !!context?.state.panel.id;
};

export const useGetCurrentPreviewPanel = () => {
	const context = useGetPreviewPanelsContext();
	return context?.state.panel ?? initialState.panel;
};
